// Client-side preview of a dedicated assistant's mirrored schedule.
//
// On save, replace_crew_assignments materialises real mirror rows for each
// assistant (see migration 20260612120100). But producers want to SEE the
// mirrored blocks in the schedule creator the moment they link an assistant —
// without saving first. This pure helper derives those preview blocks from the
// assisted member's current (in-editor) assignments, so the creator can render
// them read-only. The derived objects are never persisted and never enter the
// save payload; they exist only for rendering.

export interface AssistantLink {
  /** crew_members.id of the assistant (runner). */
  assistantId: string;
  /** crew_members.id of the assisted (filming) member. */
  sourceMemberId: string;
  /** Display name of the assisted member, for the "Mirrored from X" badge. */
  sourceName: string;
}

/** Transient marker fields added to a derived mirror preview block. */
export interface MirrorMarker {
  __mirror: true;
  __mirrorSourceName: string;
}

/**
 * Build the preview mirror blocks from two sources:
 *  - whole-day links: clone every (non-excluded) assignment of the assisted
 *    member onto the assistant;
 *  - per-assignment assistants: clone a single source assignment onto each id
 *    in its `assist_crew_member_ids`.
 *
 * Source rows that are themselves mirrors are skipped (no chains). Returns a
 * flat list of preview blocks. `sourceNameByMemberId` resolves the assisted
 * member's name for the per-assignment "Mirrored from X" label.
 */
export function deriveMirrorAssignments<
  T extends {
    crew_member_id: string;
    mirrored_from_id?: string | null;
    mirror_excluded?: boolean;
    assist_crew_member_ids?: string[] | null;
  },
>(
  assignments: T[],
  links: AssistantLink[],
  sourceNameByMemberId?: Map<string, string>,
): Array<T & MirrorMarker> {
  const out: Array<T & MirrorMarker> = [];
  // One preview per (assistant, source row), no matter how many link kinds
  // point at it. An assistant who both whole-day-links to a member AND is on
  // a slot's assist_crew_member_ids used to yield two identical previews —
  // the stack then split one level deeper than the visible blocks (a phantom
  // "gap" lane), matching the server, which also materialises one mirror row.
  const seen = new Set<string>();
  const key = (assistantId: string, sourceIdx: number) => `${assistantId}|${sourceIdx}`;

  // Whole-day links.
  for (const link of links) {
    if (!link.assistantId || !link.sourceMemberId) continue;
    assignments.forEach((a, idx) => {
      if (a.crew_member_id !== link.sourceMemberId) return;
      if (a.mirrored_from_id) return; // never mirror a mirror
      if (a.mirror_excluded) return; // slot opted out of mirroring
      if (seen.has(key(link.assistantId, idx))) return;
      seen.add(key(link.assistantId, idx));
      out.push({
        ...a,
        crew_member_id: link.assistantId,
        __mirror: true,
        __mirrorSourceName: link.sourceName,
      });
    });
  }

  // Per-assignment assistants.
  assignments.forEach((a, idx) => {
    if (a.mirrored_from_id) return;
    const ids = a.assist_crew_member_ids ?? [];
    if (ids.length === 0) return;
    const sourceName = sourceNameByMemberId?.get(a.crew_member_id) ?? "";
    for (const assistantId of ids) {
      if (!assistantId) continue;
      if (seen.has(key(assistantId, idx))) continue;
      seen.add(key(assistantId, idx));
      out.push({
        ...a,
        crew_member_id: assistantId,
        __mirror: true,
        __mirrorSourceName: sourceName,
      });
    }
  });

  return out;
}
